import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, ArrowRight, Link2, Info, ChevronDown, ChevronRight } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { knowledgeService } from '@/services/kbService';

import { logger } from '@/utils/logger';
// Staging document relationship structure (from PostgreSQL extracted_relationships)
export interface StagingRelationship {
  relationship_id: string;
  source_entity: string;
  target_entity: string;
  relationship_type: string;
  confidence: number;
  context?: string;
}

// API relationship structure
export interface EntityRelationshipDetail {
  source: {
    id: string;
    value: string;
    type?: string;
  };
  target: {
    id: string;
    value: string;
    type?: string;
  };
  relationship: {
    type: string;
    confidence: number;
    context?: string;
  };
}

interface EntityRelationshipsTabProps {
  documentId?: string;
  stagingRelationships?: StagingRelationship[];
  isStaging?: boolean;
}

// Relationship type color scheme
const RELATIONSHIP_COLORS: Record<string, string> = {
  WORKS_AT: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  MANAGES: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  USES: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  APPLIES: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  LOCATED_AT: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
  OWNS: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  REQUIRES: 'bg-red-500/15 text-red-400 border-red-500/30',
  SERVICES: 'bg-pink-500/15 text-pink-400 border-pink-500/30',
  MENTIONS: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30',
  RELATES_TO: 'bg-gray-500/15 text-gray-400 border-gray-500/30',
  RELATED_TO: 'bg-gray-500/15 text-gray-400 border-gray-500/30',
};

// Entity type color scheme
const ENTITY_TYPE_COLORS: Record<string, string> = {
  PERSON: 'bg-pink-500/15 text-pink-400',
  ORGANIZATION: 'bg-orange-500/15 text-orange-400',
  LOCATION: 'bg-lime-500/15 text-lime-400',
  GPE: 'bg-lime-500/15 text-lime-400',
  DATE: 'bg-amber-500/15 text-amber-400',
  MONEY: 'bg-violet-500/15 text-violet-400',
  PRODUCT: 'bg-teal-500/15 text-teal-400',
  EVENT: 'bg-red-500/15 text-red-400',
  TAG: 'bg-gray-500/15 text-gray-400',
  TOPIC: 'bg-blue-500/15 text-blue-400',
  EQUIPMENT: 'bg-teal-500/15 text-teal-400',
  CHEMICAL: 'bg-red-500/15 text-red-400',
  SERVICE: 'bg-purple-500/15 text-purple-400',
  MATERIAL: 'bg-orange-500/15 text-orange-400',
  CUSTOMER: 'bg-emerald-500/15 text-emerald-400',
  SITE: 'bg-cyan-500/15 text-cyan-400',
};

const formatRelationshipType = (type: string): string => {
  return type
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const getRelationshipColor = (type: string): string => {
  return RELATIONSHIP_COLORS[type] || RELATIONSHIP_COLORS[type?.toUpperCase()] || 'bg-gray-500/15 text-gray-400 border-gray-500/30';
};

const getEntityTypeColor = (type: string): string => {
  return ENTITY_TYPE_COLORS[type] || ENTITY_TYPE_COLORS[type?.toUpperCase()] || 'bg-gray-500/15 text-gray-400';
};

const getConfidenceColor = (confidence: number): string => {
  if (confidence >= 0.8) return 'bg-green-500';
  if (confidence >= 0.6) return 'bg-yellow-500';
  return 'bg-red-500';
};

// Render a single relationship card
const RelationshipCard: React.FC<{
  relationship: EntityRelationshipDetail | StagingRelationship;
}> = ({ relationship }) => {
  const [isContextOpen, setIsContextOpen] = useState(false);

  // Normalize the relationship structure
  const isApiFormat = 'source' in relationship && typeof relationship.source === 'object';

  let sourceValue: string;
  let sourceType: string | undefined;
  let targetValue: string;
  let targetType: string | undefined;
  let relType: string;
  let confidence: number;
  let context: string | undefined;

  if (isApiFormat) {
    const apiRel = relationship as EntityRelationshipDetail;
    sourceValue = apiRel.source.value;
    sourceType = apiRel.source.type;
    targetValue = apiRel.target.value;
    targetType = apiRel.target.type;
    relType = apiRel.relationship.type;
    confidence = apiRel.relationship.confidence;
    context = apiRel.relationship.context;
  } else {
    const stagingRel = relationship as StagingRelationship;
    sourceValue = stagingRel.source_entity;
    targetValue = stagingRel.target_entity;
    relType = stagingRel.relationship_type;
    confidence = stagingRel.confidence;
    context = stagingRel.context;
  }

  const confidencePercent = Math.round(confidence * 100);

  return (
    <Card className="mb-3">
      <CardContent className="py-3 px-4">
        <div className="flex items-center flex-wrap gap-2">
          {/* Source Entity */}
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-sm">{sourceValue}</span>
            {sourceType && (
              <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0", getEntityTypeColor(sourceType))}>
                {sourceType}
              </Badge>
            )}
          </div>

          {/* Relationship Arrow with Type */}
          <div className="flex items-center gap-1.5 mx-2">
            <ArrowRight className="h-3 w-3 text-muted-foreground" />
            <Badge variant="outline" className={cn("text-xs", getRelationshipColor(relType))}>
              {formatRelationshipType(relType)}
            </Badge>
            <ArrowRight className="h-3 w-3 text-muted-foreground" />
          </div>

          {/* Target Entity */}
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-sm">{targetValue}</span>
            {targetType && (
              <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0", getEntityTypeColor(targetType))}>
                {targetType}
              </Badge>
            )}
          </div>

          {/* Confidence Badge */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  className={cn(
                    "ml-auto text-white text-[10px] px-1.5",
                    getConfidenceColor(confidence)
                  )}
                >
                  {confidencePercent}%
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="dark">
                <p>Confidence: {confidencePercent}%</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Context (collapsible) */}
        {context && context.trim() && (
          <Collapsible open={isContextOpen} onOpenChange={setIsContextOpen} className="mt-2">
            <CollapsibleTrigger className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
              {isContextOpen ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
              <Info className="h-3 w-3" />
              <span>Context</span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <p className="text-xs text-muted-foreground mt-2 p-2 bg-muted rounded italic">
                "{context}"
              </p>
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  );
};

const EntityRelationshipsTab: React.FC<EntityRelationshipsTabProps> = ({
  documentId,
  stagingRelationships,
  isStaging = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [relationships, setRelationships] = useState<EntityRelationshipDetail[]>([]);

  // Fetch relationships for approved documents
  useEffect(() => {
    if (!isStaging && documentId) {
      fetchRelationships();
    }
  }, [documentId, isStaging]);

  const fetchRelationships = async () => {
    if (!documentId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await knowledgeService.getEntityRelationships(documentId);
      setRelationships(response.relationships || []);
    } catch (err: any) {
      logger.error('Failed to fetch entity relationships:', err);
      setError('Failed to load entity relationships');
      setRelationships([]);
    } finally {
      setLoading(false);
    }
  };

  // Use staging relationships if provided, otherwise use fetched relationships
  const displayRelationships = isStaging ? stagingRelationships : relationships;
  const relationshipCount = displayRelationships?.length || 0;

  // Group relationships by type for summary
  const groupByType = () => {
    if (!displayRelationships || displayRelationships.length === 0) return {};

    const groups: Record<string, number> = {};
    displayRelationships.forEach((rel) => {
      const type = isStaging
        ? (rel as StagingRelationship).relationship_type
        : (rel as EntityRelationshipDetail).relationship.type;
      groups[type] = (groups[type] || 0) + 1;
    });
    return groups;
  };

  const typeGroups = groupByType();

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Loading entity relationships...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>{error}</p>
      </div>
    );
  }

  if (relationshipCount === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Link2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>No entity-to-entity relationships found in this document</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Header */}
      <Card className="bg-muted/50">
        <CardContent className="py-3 space-y-2">
          <div className="flex items-center gap-2">
            <Link2 className="h-4 w-4 text-cyan-400" />
            <span className="font-semibold">
              {relationshipCount} Entity Relationship{relationshipCount !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Relationship type summary */}
          <div className="flex flex-wrap gap-2">
            {Object.entries(typeGroups).map(([type, count]) => (
              <Badge key={type} variant="outline" className={cn("text-xs", getRelationshipColor(type))}>
                {formatRelationshipType(type)}: {count}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Relationships List */}
      <div className="max-h-[50vh] overflow-y-auto pr-4" style={{ scrollbarGutter: 'stable' }}>
        {displayRelationships?.map((rel, index) => (
          <RelationshipCard
            key={isStaging
              ? (rel as StagingRelationship).relationship_id
              : `${(rel as EntityRelationshipDetail).source.id}-${(rel as EntityRelationshipDetail).target.id}-${index}`
            }
            relationship={rel}
          />
        ))}
      </div>
    </div>
  );
};

export default EntityRelationshipsTab;
